import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import LobbyPage from '@/app/pages/LobbyPage/LobbyPage';
import { PrimedCapture, stopTracks } from '@/app/capture/primedCapture';
import { CaptureTrigger, reportCaptureOutcome } from '@/app/capture/reportCapture';
import { Permission, Permissions } from '@/app/types';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';
import { formatDate } from '@/app/utils/date';
import { isChromium } from '@/app/utils/browser';
import { isBrowserRefusal, isDeviceAbsent } from '@/app/utils/errors';
import { readCapturePermissionState } from '@/app/utils/permissions';
import { EVENTS, gtag } from '@/lib/gtag';
import SentryImportPromise from '@/sentry/importPromise';

interface ILobbyPageProps {
  onJoin(permissions: Permissions, capture: PrimedCapture): void;
}

export interface Refusal {
  requested: Permissions;
  isBlockedInSettings: boolean;
}

type HeldTracks = Record<Permission, MediaStreamTrack[]>;

const NOT_SETTLED: Permissions = { audio: false, video: false };
const KINDS: Permission[] = ['audio', 'video'];

const requestedKinds = (requested: Permissions) => KINDS.filter((kind) => requested[kind]);

const eachKind = (kinds: Permission[], verdict: boolean): Permissions =>
  Object.fromEntries(kinds.map((kind) => [kind, verdict]));

const tracksOf = (stream: MediaStream, kind: Permission) => {
  if (kind === 'audio') {
    return stream.getAudioTracks();
  }

  return stream.getVideoTracks();
};

const isRejected = (outcome: PromiseSettledResult<unknown>): outcome is PromiseRejectedResult =>
  outcome.status === 'rejected';

const asksForBoth = (requested: Permissions) => Boolean(requested.audio && requested.video);

const reportUnexpectedFailure = async(requested: Permissions, error: unknown) => {
  const { default: Sentry } = await SentryImportPromise;

  Sentry.captureException(error, {
    tags: { operation: 'lobby-capture' },
    extra: {
      requested,
      microphonePermission: await readCapturePermissionState('microphone'),
      cameraPermission: await readCapturePermissionState('camera'),
    },
  });
};

const reportFailure = (trigger: CaptureTrigger, requested: Permissions, error: unknown) => {
  if (isDeviceAbsent(error)) {
    return;
  }
  if (isBrowserRefusal(error)) {
    reportCaptureOutcome(trigger, 'refused', requested, error).catch(() => undefined);
    return;
  }
  reportUnexpectedFailure(requested, error).catch(() => undefined);
};

export default function LobbyPageContainer({ onJoin }: ILobbyPageProps) {
  const [permissions, setPermissions] = useState(NOT_SETTLED);
  // False on first paint means "not settled yet", not "denied" — the warning
  // waits for the actual verdict so the resolved layout is the first one drawn.
  const [denied, setDenied] = useState(NOT_SETTLED);
  const [videoStream, setVideoStream] = useState<MediaStream | undefined>();
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const held = useRef<HeldTracks>({ audio: [], video: [] });
  const absent = useRef<Permissions>({});
  const isReleased = useRef(false);
  const isHandedOver = useRef(false);

  const meetingData = useMeetingData();
  const { isClient } = useUserData();

  const lobbyPageData = useMemo(() => {
    const customer = meetingData.participants.find((participant) => !participant.is_host);
    const staff = meetingData.participants.find((participant) => participant.is_host);

    return {
      startTime: formatDate(meetingData.meeting_data.appointment_start),
      address: meetingData.meeting_data.house_address,
      customerName: customer?.user_name,
      staffName: staff?.user_name,
    };
  }, [meetingData]);

  const release = useCallback((kinds: Permission[]) => {
    kinds.forEach((kind) => {
      stopTracks(held.current[kind]);
      held.current[kind] = [];
    });
  }, []);

  // A refusal outranks a capture taken earlier: the chip re-checks a granted
  // device against the browser's site controls, and a capture the customer
  // has since revoked must not count as a granted microphone at join.
  const settle = useCallback((kinds: Permission[], error: unknown) => {
    const granted = !error;
    if (!granted) {
      release(kinds);
    }
    setPermissions((prevState) => ({ ...prevState, ...eachKind(kinds, granted) }));
    setDenied((prevState) => ({ ...prevState, ...eachKind(kinds, !granted) }));
    absent.current = { ...absent.current, ...eachKind(kinds, isDeviceAbsent(error)) };
  }, [release]);

  const take = useCallback((stream: MediaStream, kinds: Permission[]) => {
    kinds.forEach((kind) => {
      stopTracks(held.current[kind]);
      held.current[kind] = tracksOf(stream, kind);
    });
    if (kinds.includes('video')) {
      setVideoStream(stream);
    }
  }, []);

  // The capture stays open on purpose: where a grant lasts no longer than the
  // capture that earned it, the tracks have to go to the call and live until
  // the SDK holds its own.
  const acquire = useCallback(async(requested: Permissions, trigger: CaptureTrigger) => {
    const kinds = requestedKinds(requested);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(requested);
    } catch (error) {
      settle(kinds, error);
      reportFailure(trigger, requested, error);
      throw error;
    }
    if (isReleased.current) {
      stopTracks(stream.getTracks());
      return;
    }
    take(stream, kinds);
    settle(kinds, null);
  }, [settle, take]);

  // One prompt covers both devices. A machine without one of them must not
  // lose the other, so that refusal alone is retried device by device.
  const requestPermissions = useCallback(async(requested: Permissions, trigger: CaptureTrigger = 'chip') => {
    try {
      await acquire(requested, trigger);
    } catch (error) {
      if (!asksForBoth(requested) || !isDeviceAbsent(error)) {
        throw error;
      }
      const outcomes = await Promise.allSettled([
        acquire({ audio: true }, trigger),
        acquire({ video: true }, trigger),
      ]);
      const failure = outcomes.find(isRejected);
      if (failure) {
        throw failure.reason;
      }
    }
  }, [acquire]);

  // Ours and the SDK's are two captures of one camera, and Android gives that
  // camera to one of them at a time. Where a grant lasts no longer than the
  // capture that earned it, releasing would answer the SDK with a second
  // prompt mid-join instead, so only Chromium gets the camera back. The
  // microphone is not the contended device and goes across everywhere.
  const join = useCallback(() => {
    isHandedOver.current = true;
    const granted = { audio: held.current.audio.length > 0, video: held.current.video.length > 0 };
    if (isChromium()) {
      release(['video']);
    }
    gtag(EVENTS.JOIN_CHANNEL, granted);
    onJoin(granted, { audio: held.current.audio, video: held.current.video });
  }, [onJoin, release]);

  const explainRefusal = useCallback(async(requested: Permissions) => {
    const [microphone, camera] = await Promise.all([
      readCapturePermissionState('microphone'),
      readCapturePermissionState('camera'),
    ]);
    const isBlockedInSettings = Boolean(requested.audio && microphone === 'denied') ||
      Boolean(requested.video && camera === 'denied');
    setRefusal({ requested, isBlockedInSettings });
  }, []);

  // The request runs inside the tap on purpose: after a refusal WebKit denies
  // every further request outright, except one made under a user gesture,
  // which gets the prompt again. This is the customer's second chance, and
  // the SDK cannot take it — its request runs from an effect, outside the
  // gesture.
  const askThenJoin = useCallback((trigger: 'join' | 'retry') => {
    const missingKinds = KINDS.filter((kind) => !held.current[kind].length && !absent.current[kind]);
    if (!missingKinds.length) {
      join();
      return;
    }
    const missing = eachKind(missingKinds, true);
    requestPermissions(missing, trigger).then(
      () => {
        reportCaptureOutcome(trigger, 'granted', missing).catch(() => undefined);
        join();
      },
      () => explainRefusal(missing),
    );
  }, [explainRefusal, join, requestPermissions]);

  const handleJoinCall = useCallback(() => askThenJoin('join'), [askThenJoin]);

  const handleTryAgain = useCallback(() => {
    setRefusal(null);
    askThenJoin('retry');
  }, [askThenJoin]);

  const handleJoinWithout = useCallback(() => {
    if (refusal) {
      reportCaptureOutcome('join', 'joined-without', refusal.requested).catch(() => undefined);
    }
    setRefusal(null);
    join();
  }, [join, refusal]);

  const handleStay = useCallback(() => setRefusal(null), []);

  // A refusal is expected and already visible in the checklist, which offers
  // the retry.
  useEffect(function requestPermissionsOnMount() {
    requestPermissions({ audio: true, video: true }, 'mount').catch(() => undefined);
  }, [requestPermissions]);

  useEffect(function releaseOnLeave() {
    isReleased.current = false;
    const tracks = held.current;

    return () => {
      isReleased.current = true;
      if (isHandedOver.current) {
        return;
      }
      KINDS.forEach((kind) => stopTracks(tracks[kind]));
    };
  }, []);

  return (
    <LobbyPage
      {...lobbyPageData}
      denied={denied}
      isClient={isClient}
      onJoin={handleJoinCall}
      onJoinWithout={handleJoinWithout}
      onStay={handleStay}
      onTryAgain={handleTryAgain}
      permissions={permissions}
      refusal={refusal}
      requestPermissions={requestPermissions}
      videoStream={videoStream} />
  );
}
